/**
 * FP3-037 correction — Super Admin "Add Organization" modal.
 *
 * Mounts from the Platform Control Center. Calls the existing
 * super-admin-only POST /api/organizations and lets the platform
 * owner capture optional onboarding context (primary contact,
 * notes) plus the access posture (access type, billing interval,
 * trial dates, membership status).
 *
 * Important: this DOES NOT mint a Stripe customer or subscription.
 * The dashboard surfaces a clear "Stripe: Not connected" status
 * for the new org because Stripe Billing v1 has not shipped yet
 * (see FP3-037 audit doc).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Buildings } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const ACCOUNT_TYPES = [
  { value: "school_team", label: "School / district team" },
  { value: "district", label: "District" },
  { value: "individual_provider", label: "Individual provider" },
];

const ACCESS_TYPES = [
  { value: "trial", label: "Trial" },
  { value: "pilot", label: "Pilot" },
  { value: "limited", label: "Limited" },
  { value: "membership", label: "Full membership" },
];

const MEMBERSHIP_STATUSES = [
  { value: "", label: "Default (auto)" },
  { value: "trialing", label: "Trialing" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "inactive", label: "Inactive" },
  { value: "not_required", label: "Not required" },
];

const BILLING_INTERVALS = [
  { value: "none", label: "None / not billed" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
];

export default function AddOrganizationModal({ open, onClose, onCreated }) {
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState("school_team");
  const [primaryName, setPrimaryName] = useState("");
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [accessType, setAccessType] = useState("pilot");
  const [membershipStatus, setMembershipStatus] = useState("");
  const [billingInterval, setBillingInterval] = useState("none");
  const [trialStart, setTrialStart] = useState("");
  const [trialEnd, setTrialEnd] = useState("");
  const [defaultSchool, setDefaultSchool] = useState("");

  useEffect(() => {
    if (!open) return;
    setName("");
    setAccountType("school_team");
    setPrimaryName("");
    setPrimaryEmail("");
    setPrimaryPhone("");
    setNotes("");
    setAccessType("pilot");
    setMembershipStatus("");
    setBillingInterval("none");
    setTrialStart("");
    setTrialEnd("");
    setDefaultSchool("");
  }, [open]);

  const emailValid = !primaryEmail.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(primaryEmail.trim());
  const canSubmit = !!name.trim() && emailValid && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        account_type: accountType,
        access_type: accessType,
        billing_interval: billingInterval,
        trial_starts_at: trialStart || null,
        trial_ends_at: trialEnd || null,
        primary_contact_name: primaryName.trim() || null,
        primary_contact_email: primaryEmail.trim().toLowerCase() || null,
        primary_contact_phone: primaryPhone.trim() || null,
        notes: notes.trim() || null,
        membership_status: membershipStatus || null,
      };
      const res = await api.createOrganization(payload);
      const newOrg = res?.data;
      toast.success(`Organization "${newOrg?.name || name}" created`);

      // Optionally create the default school under the new org. We
      // do this synchronously so the dashboard refresh picks up
      // both rows immediately. Failures here are non-fatal — the
      // org itself was created successfully.
      if (newOrg?.id && defaultSchool.trim()) {
        try {
          await api.createAdminSchool({
            organization_id: newOrg.id,
            name: defaultSchool.trim(),
          });
          toast.success(`Campus "${defaultSchool.trim()}" added`);
        } catch (schoolErr) {
          toast.error(
            `Org created but campus failed: ${schoolErr?.response?.data?.detail || schoolErr.message}`
          );
        }
      }
      onCreated?.(newOrg);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          "Could not create organization"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="add-organization-modal"
        className="max-w-lg max-h-[90vh] overflow-y-auto p-0 border-[#E5E3DB] rounded-2xl"
      >
        <div className="px-5 py-4 border-b border-[#E5E3DB] flex items-center gap-2.5 bg-[#FBFAF6]">
          <div className="w-8 h-8 rounded-lg bg-[#1F4F73] flex items-center justify-center">
            <Buildings size={16} weight="duotone" className="text-white" />
          </div>
          <div>
            <DialogTitle className="text-[15px] font-semibold text-[#1F2937] heading-font">
              Add organization
            </DialogTitle>
            <DialogDescription className="text-[12px] text-[#6B7280] mt-0.5">
              Platform-owner action. Creates the organization; Stripe billing is not minted (manual billing only until v1 ships).
            </DialogDescription>
          </div>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-3">
          <Field label="Organization name *" testid="field-name">
            <Input
              data-testid="add-org-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="e.g., Westfield USD"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Account type">
              <Select
                testid="add-org-account-type"
                value={accountType}
                onChange={setAccountType}
                options={ACCOUNT_TYPES}
              />
            </Field>
            <Field label="Access type">
              <Select
                testid="add-org-access-type"
                value={accessType}
                onChange={setAccessType}
                options={ACCESS_TYPES}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Membership status">
              <Select
                testid="add-org-membership-status"
                value={membershipStatus}
                onChange={setMembershipStatus}
                options={MEMBERSHIP_STATUSES}
              />
            </Field>
            <Field label="Billing interval">
              <Select
                testid="add-org-billing-interval"
                value={billingInterval}
                onChange={setBillingInterval}
                options={BILLING_INTERVALS}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Trial start">
              <Input
                data-testid="add-org-trial-start"
                type="date"
                value={trialStart}
                onChange={(e) => setTrialStart(e.target.value)}
              />
            </Field>
            <Field label="Trial end">
              <Input
                data-testid="add-org-trial-end"
                type="date"
                value={trialEnd}
                onChange={(e) => setTrialEnd(e.target.value)}
              />
            </Field>
          </div>

          <div className="pt-2 border-t border-[#F1F2F4]">
            <p className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold mb-2">
              Primary contact (optional)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <Input
                  data-testid="add-org-primary-name"
                  value={primaryName}
                  onChange={(e) => setPrimaryName(e.target.value)}
                  placeholder="Full name"
                />
              </Field>
              <Field label="Email">
                <Input
                  data-testid="add-org-primary-email"
                  type="email"
                  value={primaryEmail}
                  onChange={(e) => setPrimaryEmail(e.target.value)}
                  placeholder="name@school.org"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Phone">
                <Input
                  data-testid="add-org-primary-phone"
                  value={primaryPhone}
                  onChange={(e) => setPrimaryPhone(e.target.value)}
                  placeholder="(optional)"
                />
              </Field>
              <Field label="Default school / campus">
                <Input
                  data-testid="add-org-default-school"
                  value={defaultSchool}
                  onChange={(e) => setDefaultSchool(e.target.value)}
                  placeholder="(optional)"
                />
              </Field>
            </div>
          </div>

          <Field label="Notes">
            <textarea
              data-testid="add-org-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Onboarding context, special access conditions, etc."
              className="w-full text-[13px] px-3 py-2 rounded-md border border-[#E5E3DB] focus:outline-none focus:ring-2 focus:ring-[#1F4F73]/30 focus:border-[#1F4F73] bg-white"
            />
          </Field>

          <div className="rounded-md bg-[#F2F6F9] border border-[#D7DFE6] px-3 py-2.5 text-[11.5px] text-[#3F4756]">
            <strong className="text-[#1F4F73]">Stripe is not connected.</strong>{" "}
            This org will be billed manually until Stripe Billing v1 ships. The
            stripe_customer_id / stripe_subscription_id fields stay empty by design.
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              data-testid="add-org-cancel"
              variant="outline"
              onClick={() => !submitting && onClose?.()}
              className="border-[#E5E3DB]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="add-org-submit"
              disabled={!canSubmit}
              className="bg-[#1F4F73] hover:bg-[#143D5B] text-white"
            >
              {submitting ? "Creating…" : "Create organization"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[11px] font-semibold text-[#3F4756] mb-1">{label}</span>
      {children}
    </label>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-[13px] px-3 py-2 rounded-md border border-[#E5E3DB] focus:outline-none focus:ring-2 focus:ring-[#1F4F73]/30 focus:border-[#1F4F73] bg-white"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
